import BigModal from "../common/modals/BigModal";
import FloatingLabelInput from "../common/inputs/FloatingLabelInput";
import FloatingLabelInputGroup from "../common/inputs/FloatingLabelInputGroup";
import FloatingLabelInputGroupWithButton from "../common/inputs/FloatingLabelInputGroupWithButton";
import config from "../../config";
import useFetchBookings from '../../hooks/useFetchBookings';
import { useState, useEffect } from "react";

const BookingOverviewModal = ({ t, booking, onClose }) => {

  const state = booking !== null ? booking.Status : "";
  const cancellationOptions = t("pages.bookings.actions.cancellation.states", { returnObjects: true });
  const confirmationOptions = t("pages.bookings.actions.confirmation.states", { returnObjects: true });
  const showCancelButton = cancellationOptions.includes(state);
  const showContinueButton = confirmationOptions.includes(state);
  const showCloseButton = (showCancelButton !== showContinueButton) || (!showCancelButton && !showContinueButton);
  
  const { manageCabinBooking } = useFetchBookings();
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [extras, setExtras] = useState([]);

  useEffect(() => {
    if (booking && booking.Extras) {
      try {
        const parsedExtras = JSON.parse(booking.Extras);
        setExtras(parsedExtras);
      } catch (e) {
        console.error("Error reading extras:", e);
      }
    }
  }, [booking]);

  const isFound = booking !== null;
  const logo = <div className="big-modal-main"><img src={config.globalLogo} alt="Logo"></img></div>

  const handleCancelCabin = async (index) => {
    if(!window.confirm(t("pages.booking_overview.modal.cancel_cabin"))) { return }
    const extra = booking.Extras && JSON.parse(booking.Extras)[index];
    if (extra) {
      try {
        setLoadingIndex(index);
        await manageCabinBooking(extra.ResourceId, booking.Email, booking.BookingCode, booking.StartDate, booking.EndDate, extra.StartTime, extra.EndTime, "2"); // "2" to cancel
        setExtras((prevExtras) => prevExtras.filter((_, i) => i !== index));
      } catch (error) { 
        // NOTHING
      } finally {
        setLoadingIndex(null);
      }
    }
  };

  const foundBody = (isFound &&
    <div className="big-modal-main">
      <h2>{t("pages.booking_overview.modal.text_found")}</h2>
      <div className="input-container">
        <FloatingLabelInputGroup 
          inputs={[
            { label: t("pages.booking_overview.modal.start_date"), value: booking.StartDate + ' ' + booking.StartTime, disabled: true },
            { label: t("pages.booking_overview.modal.end_date"), value: booking.EndDate + ' ' + booking.EndTime, disabled: true },
          ]}
        />
        <FloatingLabelInput label={t("pages.booking_overview.modal.email")} value={booking.Email} disabled />
        <FloatingLabelInput label={t("pages.booking_overview.modal.name")} value={booking.Name} disabled />
        <FloatingLabelInput label={t("pages.booking_overview.modal.amount_paid")} value={booking.AmountPaid || booking.AmountPaid === 0 ? booking.AmountPaid + ' €' : ''} disabled />
        <FloatingLabelInputGroup 
          inputs={[
            { label: t("pages.booking_overview.modal.desk"), value: booking.ResourceId, disabled: true },
            { label: t("pages.booking_overview.modal.booking_code"), value: booking.BookingCode, disabled: true },
          ]}
        />
        <FloatingLabelInput label={t("pages.booking_overview.modal.status")} value={getState(t, booking.Status)} disabled />

        
        {extras.length > 0 && (
          <>
            <div style={{ marginTop: "5px" }}> {t("pages.booking_overview.modal.extra")} : </div>
            {extras.map((extra, index) => (
              <FloatingLabelInputGroupWithButton
                key={index}
                inputs={[
                  { label: t("pages.booking_overview.modal.cabin"), value: `${extra.ResourceId} 🕒 ${extra.StartTime} - ${extra.EndTime}`, disabled: true },
                ]}
                handleClick={() => handleCancelCabin(index)}
                buttonLabel={"-"}
                loading={index === loadingIndex}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );

  const foundButton = (isFound &&
    <>
      {showCloseButton && <button onClick={onClose} className="modal-button close-modal-button">{t("pages.booking_overview.modal.close_button", { returnObjects: true })}</button>}
      {showCancelButton && <button onClick={() => {onClose('cancellation')}} className="modal-button cancel-modal-button">{t("pages.booking_overview.modal.cancel_button", { returnObjects: true })}</button>}
      {showContinueButton && <button onClick={() => {onClose('payment')}} className="modal-button continue-modal-button">{t("pages.booking_overview.modal.confirm_button", { returnObjects: true })}</button>}
    </>
  );

  const notFoundBody = (!isFound &&
    <div className="big-modal-main">
      <h2>{t("pages.booking_overview.modal.title_not_found", { returnObjects: true })}</h2>
      <div className="input-container">
        {t("pages.booking_overview.modal.text_not_found", { returnObjects: true })}
      </div>
      <img src="/images/result/decline_12510978.png" alt="Not_Found" width="50px" height="50px" style={{ marginBottom: "10px"}}/>
    </div>
  );

  const notFoundButton = (!isFound &&
    <button onClick={onClose} className="modal-button close-modal-button">{t("pages.booking_overview.modal.close_button", { returnObjects: true })}</button>
  );

  return (
    <BigModal 
      title={(booking && booking.ResourceId ? t("pages.booking_new.modal.desk") + ' ' + booking.ResourceId : '')}
      leftContent={isFound ? foundBody : notFoundBody}
      rightContent={logo}
      onClose= {onClose} 
      footerButton={isFound ? foundButton : notFoundButton}
      onBack={onClose}
    />
  );
};

export default BookingOverviewModal;

const getState = (t, state) => {
  if (state) {
    const states = t("pages.bookings.states", { returnObjects: true });
    const foundState = Object.values(states).find((s) => s.options.includes(state));
    return foundState ? foundState.text : t("pages.bookings.states.unknown.text");
  }
  return ""
}